import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { useBackendHealth } from '../hooks/useBackendHealth';
import { selectTenantSlug } from '../store/slices/tenantSlice';
import { socketIoOptions, socketBaseUrl } from '../utils/socketBaseUrl';

type DispatchEventHandler = (event: string, data: unknown) => void;

export function useDispatchSocket(onEvent?: DispatchEventHandler) {
  const socketRef = useRef<Socket | null>(null);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const { isBackendUp } = useBackendHealth();
  const tenantSlug = useSelector(selectTenantSlug);

  const connect = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token || !isBackendUp) return;

    socketRef.current?.disconnect();
    socketRef.current = null;

    const slug = tenantSlug || localStorage.getItem('tenantSlug') || 'demo';
    const base = socketBaseUrl();
    const socket = io(base, {
      auth: { token, tenantSlug: slug },
      ...socketIoOptions(),
    });

    socket.on('connect', () => {
      socket.emit('dispatch:subscribe');
    });

    socket.io.on('reconnect', () => {
      socket.emit('dispatch:subscribe');
    });

    const events = [
      'dispatch:queue:update',
      'dispatch:assignment',
      'dispatch:tracking',
      'dispatch:sos',
      'dispatch:log',
      'driver:location',
    ];

    events.forEach((event) => {
      socket.on(event, (data) => handlerRef.current?.(event, data));
    });

    socketRef.current = socket;
  }, [isBackendUp, tenantSlug]);

  useEffect(() => {
    if (!isBackendUp) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }
    connect();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [connect, isBackendUp]);

  return { reconnect: connect };
}
